import { Link } from "@tanstack/react-router";
import type { TargetCollaborationCreator } from "@unbox/api";
import { Send01, Users01, UserX01, ZapFast } from "@untitledui/icons";
import { BadgeWithIcon } from "@/components/base/badges/badges";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useGmvFormatter } from "@/hooks/use-gmv-formatter";
import { useScopedUrl } from "@/hooks/use-scoped-url";
import { stepLabel } from "@/pages/sequences/sequence-steps-preview";

const REMOVAL_SOURCE_LABELS: Record<string, string> = {
  blocklist: "Blocklist",
  safety: "Safety policy",
  tiktok: "TikTok",
};

export const RemovedByCell = ({ removedBy }: { removedBy: TargetCollaborationCreator["removedBy"] }) => {
  const scopedUrl = useScopedUrl();
  if (!removedBy) return <span className="text-sm text-tertiary">-</span>;

  if (removedBy.source === "user") {
    return <span className="text-sm text-primary whitespace-nowrap">{removedBy.user?.name ?? "A team member"}</span>;
  }

  if (removedBy.source === "automation") {
    const automationHref: string = removedBy.sequence ? `${scopedUrl}/automate-outreach/${removedBy.sequence.id}` : "";
    return (
      <HoverCard openDelay={100} closeDelay={150}>
        <HoverCardTrigger asChild>
          <span>
            <BadgeWithIcon type="pill-color" color="blue-light" size="sm" iconTrailing={ZapFast}>
              Automation
            </BadgeWithIcon>
          </span>
        </HoverCardTrigger>
        <HoverCardContent
          side="top"
          className="w-auto rounded-lg border-0 bg-primary-solid px-3 py-2 text-xs font-semibold text-white"
        >
          Removed from collab automatically by{" "}
          {removedBy.sequence ? (
            <Link to={automationHref} className="underline underline-offset-2">
              {removedBy.sequence.name}
            </Link>
          ) : (
            "an automation"
          )}
          .
        </HoverCardContent>
      </HoverCard>
    );
  }

  return (
    <span className="text-sm text-tertiary whitespace-nowrap">{REMOVAL_SOURCE_LABELS[removedBy.source] ?? "-"}</span>
  );
};

const LAST_ACTION_LABELS: Record<string, { label: string; icon: typeof Send01 }> = {
  send_message: { label: "Message", icon: Send01 },
  remove_from_collab: { label: "Removed from collab", icon: UserX01 },
  cohort_entered: { label: "Entered a cohort", icon: Users01 },
};

export const LastActionCell = ({ lastAction }: { lastAction: TargetCollaborationCreator["lastAction"] }) => {
  if (!lastAction) return <span className="text-sm text-tertiary">-</span>;
  const known = LAST_ACTION_LABELS[lastAction.type];
  const label = known?.label ?? stepLabel({ type: lastAction.type, messageTemplateId: null, customMessage: null });
  const Icon = known?.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-primary whitespace-nowrap">
      {label}
      {Icon && <Icon className="size-4 text-utility-blue-light-500" />}
    </span>
  );
};

export const GenderDemoCell = ({ topGender }: { topGender: TargetCollaborationCreator["topGender"] }) => (
  <span className="text-sm text-primary whitespace-nowrap">
    {topGender ? `${topGender.gender} - ${topGender.percentage}%` : "-"}
  </span>
);

export const GmvFromVideosCell = ({ value }: { value: number | null }) => {
  const formatGmv = useGmvFormatter({ compact: false });
  return <span className="text-sm text-primary font-medium">{value != null ? formatGmv(value) : "-"}</span>;
};
